import { useCallback } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useIxcRegistrationV1ProfileCommandServiceUpdateProfile } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-command-service-update-profile';
import { useIxcRegistrationV1ProfileQueryServiceProfileGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-query-service-profile-get';
import { ActionLayout } from '@interxfi/app-components/components/layout/action-layout/action-layout';
import { DEFAULT_PAGE_LAYOUT_GAP } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { TableOfContentsContextProvider } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { ProfileFormReview } from '../../../../../../../../../features/registration/profile/form/profile-form-review';
import {
  type PendingProfileValues,
  useProfileFormSubmissionContext,
  useResetProfileFormSubmissionContext,
  useResetProfileFormSubmissionContextOnNavigateAway,
} from '../../../../../../../../../features/registration/profile/form/profile-form-submission-context';
import { RegistrationProfileFormTableOfContents } from '../../../../../../../../../features/registration/profile/form/registration-profile-form-table-of-contents';
import {
  getRegistrationProfileDetailPath,
  getRegistrationProfileEditPath,
} from '../../../../../../../../../features/registration/profile/registration-profile-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

interface WithPendingProfile {
  pendingProfile: PendingProfileValues;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/profile/_profile/$profileId/edit/_edit/review')({
  loader: ({ params }) => {
    const { profileId } = params;
    const { pendingProfile } = useProfileFormSubmissionContext.getState();

    if (!pendingProfile?.spec) {
      throw redirect({ to: '/registration/profile/$profileId/edit', params: { profileId } });
    }

    return {
      pendingProfile,
      breadcrumb: buildBreadcrumb({
        id: `profile-${profileId}-edit-review`,
        label: 'Review',
        to: `/registration/profile/${profileId}/edit/review`,
      }),
    } satisfies AggregateBreadcrumb & WithPendingProfile;
  },
  preload: false,
  shouldReload: true,
  component: EditProfileReview,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to edit profile', 'edit this profile');

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const;

function EditProfileReview() {
  const { profileId } = Route.useParams();
  useResetProfileFormSubmissionContextOnNavigateAway(getRegistrationProfileEditPath(profileId));
  const { data: profileData } = useIxcRegistrationV1ProfileQueryServiceProfileGet({ profileId });
  const navigate = Route.useNavigate();
  const { pendingProfile } = Route.useLoaderData();
  const addToast = useAddToast();
  const { mutateAsync, isPending, error } = useIxcRegistrationV1ProfileCommandServiceUpdateProfile();
  useErrorHandler(error, errorMessageGetter);
  const [submitState, submitRef] = useButtonState(isPending, error);
  const reset = useResetProfileFormSubmissionContext();
  const isTooCompactForTableOfContents = useBreakpoint('sm');
  const replacingVersionId =
    profileData?.profile?.data?.pendingVersion?.profileVersionId ?? profileData?.profile?.data?.currentVersion?.profileVersionId;
  const profileType = getOneOfType(pendingProfile.spec);
  const handleSubmit = useCallback(async () => {
    if (!replacingVersionId) {
      return;
    }

    const result = await mutateAsync({ profileId, replacesVersionId: replacingVersionId, spec: pendingProfile.spec });

    if (result) {
      addToast({ id: 'profileEdited', content: 'Profile was updated', variant: 'success' });

      reset();

      const navigateTo = getRegistrationProfileDetailPath(result.profile.profileId);
      if (navigateTo) {
        await navigate({ to: navigateTo });
      }
    }
  }, [replacingVersionId, mutateAsync, profileId, pendingProfile.spec, addToast, reset, navigate]);

  const baseReviewElements = (
    <VerticalStack gap={4}>
      <Typography.Level1>Review Information</Typography.Level1>
      <VerticalStack gap={7}>
        <ProfileFormReview diffingProfileVersionId={replacingVersionId} pendingProfile={pendingProfile.spec} programId={pendingProfile.programId} />
      </VerticalStack>
    </VerticalStack>
  );

  return (
    <TableOfContentsContextProvider>
      <ActionLayout
        actions={[
          { buttonLink: { children: 'Back', id: 'back', LinkElement: Link, to: getRegistrationProfileEditPath(profileId), variant: 'secondary' } },
          { button: { children: 'Update Profile', id: 'submit', onClick: handleSubmit, ref: submitRef, state: submitState, variant: 'primary' } },
        ]}
        maxWidthContainer="1040px"
      >
        {isTooCompactForTableOfContents || !profileType ? (
          baseReviewElements
        ) : (
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: baseReviewElements,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...rightColumnContainer,
              render: (isWrapping) => (isWrapping ? null : <RegistrationProfileFormTableOfContents selectedProfileType={profileType} />),
            }}
            wrap="forward"
          />
        )}
      </ActionLayout>
    </TableOfContentsContextProvider>
  );
}
